import React, { useState, useRef, useEffect } from 'react';
import AdUnit from '../components/AdUnit';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import TextareaWithLines from '../components/TextareaWithLines';
import PreWithLines from '../components/PreWithLines';
import { parseInputToJavaScriptObject } from '../utils/jsonSmartParse';

// Parses JSON that may be well-formed already, or messy/escaped text (e.g.
// a JSON value pasted from logs or an LLM response, with nested JSON stuck
// inside a string field). Falls back to the smart recovery parser only when
// strict parsing fails, so normal, valid JSON is never altered.
const smartParseJson = (input) => {
  try {
    return JSON.parse(input);
  } catch (strictError) {
    return parseInputToJavaScriptObject(input);
  }
};

// Helper functions for case conversion (placed at module level)
const toPascalCase = (str) => {
  if (!str) return "Unnamed";
  let s = String(str)
    .replace(/[^a-zA-Z0-9\s_-]/g, '') // Keep alphanumeric, space, underscore, hyphen
    .replace(/[\s_-]+(.)/g, (_match, char) => char.toUpperCase()) // Convert snake_case, kebab-case, space case to camel segments
    .replace(/^[a-z]/, (match) => match.toUpperCase()); // Ensure first letter is uppercase
  s = s.replace(/[^a-zA-Z0-9]/g, ''); // Remove any remaining non-alphanumeric characters
  if (!s) return "Unnamed"; // If string becomes empty after sanitization
  if (/^\d/.test(s)) s = "Field" + s; // Prepend "Field" if starts with a digit
  return s;
};

const toCamelCase = (str) => {
  const pascal = toPascalCase(str);
  if (!pascal || pascal === "Unnamed") return "unnamed";
  return pascal.charAt(0).toLowerCase() + pascal.slice(1);
};

const BeautifierPage = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [formattedJson, setFormattedJson] = useState('');
  const [error, setError] = useState('');
  const [tabSize, setTabSize] = useState(2);
  const [showConvertDropdown, setShowConvertDropdown] = useState(false);
  const [outputFormat, setOutputFormat] = useState('json');
  const [convertedOutput, setConvertedOutput] = useState('');
  const fileInputRef = useRef(null);
  const convertDropdownRef = useRef(null);
  
  // Refs for scroll syncing
  const inputRef = useRef(null);
  const outputRef = useRef(null);
  const inputLineNumbersRef = useRef(null);
  const outputLineNumbersRef = useRef(null);
  const [isInputScrolling, setIsInputScrolling] = useState(false);
  const [isOutputScrolling, setIsOutputScrolling] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (convertDropdownRef.current && !convertDropdownRef.current.contains(event.target)) {
        setShowConvertDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Enhanced scroll synchronization
  const handleInputScroll = () => {
    if (inputLineNumbersRef.current && inputRef.current) {
      inputLineNumbersRef.current.scrollTop = inputRef.current.scrollTop;
    }

    if (!isOutputScrolling && outputRef.current && inputRef.current) {
      setIsInputScrolling(true);
      const inputScrollTop = inputRef.current.scrollTop;
      const inputScrollHeight = inputRef.current.scrollHeight;
      const inputClientHeight = inputRef.current.clientHeight;
      
      const outputScrollHeight = outputRef.current.scrollHeight;
      const outputClientHeight = outputRef.current.clientHeight;
      
      const scrollRatio = inputScrollTop / (inputScrollHeight - inputClientHeight || 1);
      const outputScrollTop = scrollRatio * (outputScrollHeight - outputClientHeight || 1);
      
      outputRef.current.scrollTop = outputScrollTop;
      
      if (outputLineNumbersRef.current) {
        outputLineNumbersRef.current.scrollTop = outputScrollTop;
      }
      
      setTimeout(() => setIsInputScrolling(false), 50);
    }
  };

  const handleOutputScroll = () => {
    if (outputLineNumbersRef.current && outputRef.current) {
      outputLineNumbersRef.current.scrollTop = outputRef.current.scrollTop;
    }

    if (!isInputScrolling && inputRef.current && outputRef.current) {
      setIsOutputScrolling(true);
      const outputScrollTop = outputRef.current.scrollTop;
      const outputScrollHeight = outputRef.current.scrollHeight;
      const outputClientHeight = outputRef.current.clientHeight;
      
      const inputScrollHeight = inputRef.current.scrollHeight;
      const inputClientHeight = inputRef.current.clientHeight;
      
      const scrollRatio = outputScrollTop / (outputScrollHeight - outputClientHeight || 1);
      const inputScrollTop = scrollRatio * (inputScrollHeight - inputClientHeight || 1);
      
      inputRef.current.scrollTop = inputScrollTop;
      
      if (inputLineNumbersRef.current) {
        inputLineNumbersRef.current.scrollTop = inputScrollTop;
      }
      
      setTimeout(() => setIsOutputScrolling(false), 50);
    }
  };

  const beautifyJson = () => {
    try {
      setError('');
      const parsedJson = smartParseJson(jsonInput);

      const scrollPosition = window.scrollY;
      const mainContainer = document.querySelector('.beautifier-container');
      const containerHeight = mainContainer ? mainContainer.offsetHeight : null;
      
      const beautified = JSON.stringify(parsedJson, null, tabSize);
      setFormattedJson(beautified);
      setOutputFormat('json');
      setConvertedOutput('');
      
      if (mainContainer && containerHeight) {
        mainContainer.style.minHeight = `${containerHeight}px`;
      }
      setTimeout(() => window.scrollTo(0, scrollPosition), 0);
    } catch (e) {
      setError('Invalid JSON. Please check your input.');
    }
  };

  const minifyJson = () => {
    try {
      setError('');
      const parsedJson = smartParseJson(jsonInput);

      const scrollPosition = window.scrollY;
      const mainContainer = document.querySelector('.beautifier-container');
      const containerHeight = mainContainer ? mainContainer.offsetHeight : null;
      
      const minified = JSON.stringify(parsedJson);
      setFormattedJson(minified);
      setOutputFormat('json');
      setConvertedOutput('');
      
      if (mainContainer && containerHeight) {
        mainContainer.style.minHeight = `${containerHeight}px`;
      }
      setTimeout(() => window.scrollTo(0, scrollPosition), 0);
    } catch (e) {
      setError('Invalid JSON. Please check your input.');
    }
  };

  const validateJson = () => {
    try {
      JSON.parse(jsonInput);
      setError('JSON is valid!');
    } catch (e) {
      setError('Invalid JSON: ' + e.message);
    }
  };

  const downloadJson = () => {
    try {
      let content = '';
      let fileExtension = 'json';
      
      if (outputFormat === 'json') {
        content = formattedJson || jsonInput;
      } else {
        content = convertedOutput;
        if (outputFormat === 'csharp') {
          fileExtension = 'cs';
        } else if (outputFormat === 'java') {
          fileExtension = 'java';
        } else {
          fileExtension = outputFormat; // xml, yaml, csv
        }
      }
      
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `converted.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Error downloading: ' + e.message);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setJsonInput(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const renderLineNumbers = (text, lineNumbersRefParam) => {
    // This function is no longer used directly as TextareaWithLines/PreWithLines handle it
    return null;
  };

  const toggleConvertDropdown = () => {
    setShowConvertDropdown(!showConvertDropdown);
  };

  const jsonToXml = (json) => {
    try {
      const obj = typeof json === 'string' ? JSON.parse(json) : json;
      
      const convertObjToXml = (objToConvert, indent = '') => {
        let xml = '';
        for (const key in objToConvert) {
          if (objToConvert[key] === null) {
            xml += `${indent}<${key}></${key}>\n`;
          } else if (typeof objToConvert[key] === 'object') {
            if (Array.isArray(objToConvert[key])) {
              for (const item of objToConvert[key]) {
                if (typeof item === 'object') {
                  xml += `${indent}<${key}>\n${convertObjToXml(item, indent + '  ')}${indent}</${key}>\n`;
                } else {
                  xml += `${indent}<${key}>${item}</${key}>\n`;
                }
              }
            } else {
              xml += `${indent}<${key}>\n${convertObjToXml(objToConvert[key], indent + '  ')}${indent}</${key}>\n`;
            }
          } else {
            xml += `${indent}<${key}>${objToConvert[key]}</${key}>\n`;
          }
        }
        return xml;
      };
      
      const xmlString = `<?xml version="1.0" encoding="UTF-8"?>\n<root>\n${convertObjToXml(obj, '  ')}</root>`;
      return xmlString;
    } catch (e) {
      setError('Error converting to XML: ' + e.message);
      return '';
    }
  };

  const jsonToYaml = (json) => {
    try {
      const obj = typeof json === 'string' ? JSON.parse(json) : json;
      
      const convertObjToYaml = (objToConvert, indent = '') => {
        let yaml = '';
        for (const key in objToConvert) {
          if (objToConvert[key] === null) {
            yaml += `${indent}${key}: null\n`;
          } else if (typeof objToConvert[key] === 'object') {
            if (Array.isArray(objToConvert[key])) {
              yaml += `${indent}${key}:\n`;
              for (const item of objToConvert[key]) {
                if (typeof item === 'object') {
                  yaml += `${indent}- \n${convertObjToYaml(item, indent + '  ')}`;
                } else {
                  yaml += `${indent}- ${item}\n`;
                }
              }
            } else {
              yaml += `${indent}${key}:\n${convertObjToYaml(objToConvert[key], indent + '  ')}`;
            }
          } else {
            yaml += `${indent}${key}: ${objToConvert[key]}\n`;
          }
        }
        return yaml;
      };
      
      return convertObjToYaml(obj);
    } catch (e) {
      setError('Error converting to YAML: ' + e.message);
      return '';
    }
  };

  const jsonToCsv = (json) => {
    try {
      const obj = typeof json === 'string' ? JSON.parse(json) : json;
      
      const arr = Array.isArray(obj) ? obj : [obj];
      
      if (arr.length === 0) return '';
      
      const headers = [];
      arr.forEach(item => {
        if (typeof item === 'object' && item !== null) {
            Object.keys(item).forEach(key => {
              if (!headers.includes(key)) {
                headers.push(key);
              }
            });
        }
      });
      
      let csv = headers.join(',') + '\n';
      
      arr.forEach(item => {
        if (typeof item === 'object' && item !== null) {
            const row = headers.map(header => {
              const value = item[header];
              if (value === null || value === undefined) return '';
              if (typeof value === 'object') return JSON.stringify(value); 
              return String(value).includes(',') ? `"${value}"` : value; 
            }).join(',');
            csv += row + '\n';
        } else {
            // Handle array of primitives if necessary, or ignore
            csv += (String(item).includes(',') ? `"${item}"` : item) + '\n';
        }
      });
      
      return csv;
    } catch (e) {
      setError('Error converting to CSV: ' + e.message);
      return '';
    }
  };

  const jsonToCSharpClass = (parsedJson) => {
    try {
      let classes = {};
      let rootClassName = "RootObject";

      function getCSharpType(value, keyNameForClass) {
        if (value === null) return "object";
        const type = typeof value;
        if (type === 'string') return 'string';
        if (type === 'number') return Number.isInteger(value) ? 'int' : 'double';
        if (type === 'boolean') return 'bool';
        if (Array.isArray(value)) {
          if (value.length === 0) return 'List<object>';
          const firstItem = value[0];
          const itemTypeName = toPascalCase(keyNameForClass + "Item");
          const itemType = getCSharpType(firstItem, itemTypeName);
          
          if (typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem)) {
             // If it's an object, itemType will be its class name generated by recursive call
             return `List<${itemType}>`;
          }
          return `List<${itemType}>`;
        }
        if (type === 'object') {
          const nestedClassName = toPascalCase(keyNameForClass);
          generateClassDefinition(value, nestedClassName, classes);
          return nestedClassName;
        }
        return 'object';
      }

      function generateClassDefinition(currentObj, className, accClasses) {
        if (accClasses[className]) return; // Already processed

        let classString = `public class ${className}\n{\n`;
        for (const key in currentObj) {
          const propertyName = toPascalCase(key);
          if (!propertyName || propertyName === "Unnamed") continue;

          const value = currentObj[key];
          // Pass key for naming potential nested classes or list item classes
          const type = getCSharpType(value, propertyName); 
          classString += `    public ${type} ${propertyName} { get; set; }\n`;
        }
        classString += '}\n';
        accClasses[className] = classString;
      }

      generateClassDefinition(parsedJson, rootClassName, classes);

      let finalCode = classes[rootClassName] || '';
      delete classes[rootClassName];
      for (const cName in classes) {
        finalCode += "\n" + classes[cName];
      }
      return finalCode.trim();
    } catch (e) {
      setError('Error converting to C# Class: ' + e.message);
      return '';
    }
  };
  
  const jsonToJavaClass = (parsedJson) => {
    try {
      let classes = {};
      let rootClassName = "RootObject";
      let usesList = false;

      function getJavaType(value, keyNameForClass) {
        if (value === null) return "Object";
        const type = typeof value;
        if (type === 'string') return 'String';
        if (type === 'number') return Number.isInteger(value) ? 'int' : 'double';
        if (type === 'boolean') return 'boolean';
        if (Array.isArray(value)) {
          usesList = true;
          if (value.length === 0) return 'List<Object>';
          const firstItem = value[0];
          const itemTypeName = toPascalCase(keyNameForClass + "Item"); // Used if firstItem is an object
          const itemType = getJavaType(firstItem, itemTypeName);

          if (typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem)) {
            return `List<${itemType}>`;
          }
          return `List<${itemType}>`;
        }
        if (type === 'object') {
          const nestedClassName = toPascalCase(keyNameForClass);
          generateClassDefinition(value, nestedClassName, classes);
          return nestedClassName;
        }
        return 'Object';
      }

      function generateClassDefinition(currentObj, className, accClasses) {
        if (accClasses[className]) return;

        let classString = `public class ${className}\n{\n`;
        let gettersSetters = "";

        for (const key in currentObj) {
          const fieldName = toCamelCase(key);
          const getterSetterBaseName = toPascalCase(key);
          if (!fieldName || fieldName === "unnamed" || !getterSetterBaseName || getterSetterBaseName === "Unnamed") continue;

          const value = currentObj[key];
          const type = getJavaType(value, getterSetterBaseName); // Pass PascalKey for class naming

          classString += `    private ${type} ${fieldName};\n`;
          gettersSetters += `\n    public ${type} get${getterSetterBaseName}() {\n`;
          gettersSetters += `        return ${fieldName};\n    }\n`;
          gettersSetters += `\n    public void set${getterSetterBaseName}(${type} ${fieldName}) {\n`;
          gettersSetters += `        this.${fieldName} = ${fieldName};\n    }\n`;
        }
        classString += gettersSetters + '}\n';
        accClasses[className] = classString;
      }

      generateClassDefinition(parsedJson, rootClassName, classes);

      let finalCode = "";
      if (usesList) {
        finalCode += "import java.util.List;\n\n";
      }
      
      finalCode += classes[rootClassName] || '';
      delete classes[rootClassName];
      for (const cName in classes) {
        finalCode += "\n" + classes[cName];
      }
      return finalCode.trim();
    } catch (e) {
      setError('Error converting to Java Class: ' + e.message);
      return '';
    }
  };

  const convertJsonTo = (format) => {
    try {
      setError('');
      if (!jsonInput.trim()) {
        setError('JSON input is empty.');
        setConvertedOutput(''); 
        setOutputFormat(format); 
        return;
      }
      
      const scrollPosition = window.scrollY;
      const mainContainer = document.querySelector('.beautifier-container');
      const containerHeight = mainContainer ? mainContainer.offsetHeight : null;
      
      const parsedJson = JSON.parse(jsonInput);
      
      let result = '';
      if (format === 'xml') {
        result = jsonToXml(parsedJson);
      } else if (format === 'yaml') {
        result = jsonToYaml(parsedJson);
      } else if (format === 'csv') {
        result = jsonToCsv(parsedJson);
      } else if (format === 'csharp') {
        result = jsonToCSharpClass(parsedJson);
      } else if (format === 'java') {
        result = jsonToJavaClass(parsedJson);
      }
      
      setOutputFormat(format);
      setConvertedOutput(result);
      setShowConvertDropdown(false);
      
      if (mainContainer && containerHeight) {
        mainContainer.style.minHeight = `${containerHeight}px`;
      }
      setTimeout(() => window.scrollTo(0, scrollPosition), 0);
    } catch (e) {
      if (e instanceof SyntaxError) {
        setError('Invalid JSON. Please check your input syntax.');
      } else {
        setError('Error during conversion: ' + e.message);
      }
      setConvertedOutput(''); 
    }
  };

  const editorStyle = {
    // ... (existing styles, not changed)
  };

  const controlButtonStyle = {
    width: '100%',
    padding: '10px',
    margin: '5px 0',
    backgroundColor: '#00BFA5',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  };

  const handleCopyPre = () => {
    const text = outputFormat === 'json' ? formattedJson : convertedOutput;
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    });
  };

  const dropdownButtonStyle = {
    width: '100%',
    padding: '10px',
    textAlign: 'left',
    border: 'none',
    borderBottom: '1px solid #555',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: 'white'
  };

  return (
    <>
      <SEO 
        title="JSON Beautifier - Format, Validate & Convert JSON"
        description="Free online JSON beautifier, validator, and converter. Format, indent, prettify your JSON, or convert JSON to XML, YAML, CSV, C# classes, and Java classes."
        keywords={["JSON beautifier", "JSON formatter", "pretty print JSON", "format JSON", "JSON indentation", "JSON validator", "JSON to XML", "JSON to YAML", "JSON to CSV", "JSON to C#", "JSON to Java class"]}
        canonicalUrl="https://yourjsontools.com/json-beautifier"
      />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 20px 0' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>JSON Beautifier</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Format, validate, and convert your JSON data. Prettify messy JSON or convert to XML, YAML, CSV, and more.
        </p>
      </div>
      
      
      <div 
        className="beautifier-container responsive-main-container"
        style={{ 
          display: 'flex', 
          height: 'calc(100vh - 150px)', 
          maxHeight: '800px',
          minHeight: '500px',
          margin: '0 auto', 
          padding: '20px',
          gap: '40px',
          boxSizing: 'border-box',
        }}
      >
        <div className="responsive-panel" style={{ 
          flex: 1, 
          position: 'relative', 
          display: 'flex', 
          flexDirection: 'column', 
          minWidth: 0,
          maxHeight: '100%',
          overflow: 'hidden'
        }}>
          <div style={{ 
            position: 'relative', 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }}>
            <TextareaWithLines
              value={jsonInput}
              onChange={e => setJsonInput(e.target.value)}
              placeholder="Enter your JSON here..."
              textareaRef={inputRef}
              lineNumbersRef={inputLineNumbersRef}
              onScroll={handleInputScroll}
              style={{ backgroundColor: '#fff' }}
              textareaProps={{ className: 'responsive-textarea' }}
              lineNumbersRefClassName="responsive-linenumbers"
            />
          </div>
        </div>

        <div className="responsive-controls" style={{ 
          width: '220px', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '0 10px',
          flexShrink: 0
        }}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            accept=".json"
          />
          <button
            onClick={handleUploadClick}
            style={controlButtonStyle}
          >
            Upload Data
          </button>
          
          <button
            onClick={validateJson}
            style={controlButtonStyle}
          >
            Validate
          </button>
          
          <select
            value={tabSize}
            onChange={(e) => setTabSize(Number(e.target.value))}
            style={{
              width: '100%',
              padding: '10px',
              margin: '5px 0',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          >
            <option value={2}>2 Tab Space</option>
            <option value={4}>4 Tab Space</option>
            <option value={6}>6 Tab Space</option>
          </select>
          
          <button
            onClick={beautifyJson}
            style={{
              ...controlButtonStyle,
              backgroundColor: '#1976D2'
            }}
          >
            Format / Beautify
          </button>
          
          <button
            onClick={minifyJson}
            style={controlButtonStyle}
          >
            Minify / Compact
          </button>
          
          <div style={{ position: 'relative', width: '100%' }} ref={convertDropdownRef}>
            <button
              onClick={toggleConvertDropdown}
              style={controlButtonStyle}
            >
              Convert JSON to-
            </button>
            {showConvertDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                width: '100%',
                backgroundColor: '#333',
                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                zIndex: 10,
                borderRadius: '4px',
              }}>
                <button onClick={() => convertJsonTo('xml')} style={dropdownButtonStyle}>XML</button>
                <button onClick={() => convertJsonTo('yaml')} style={dropdownButtonStyle}>YAML</button>
                <button onClick={() => convertJsonTo('csv')} style={dropdownButtonStyle}>CSV</button>
                <button onClick={() => convertJsonTo('csharp')} style={dropdownButtonStyle}>C# Class</button>
                <button onClick={() => convertJsonTo('java')} style={{...dropdownButtonStyle, borderBottom: 'none'}}>Java Class</button>
              </div>
            )}
          </div>
          
          <button
            onClick={downloadJson}
            style={controlButtonStyle}
          >
            Download
          </button>
          
          {error && (
            <div style={{ 
              padding: '10px', 
              marginTop: '10px', 
              color: error === 'JSON is valid!' ? 'green' : 'red',
              textAlign: 'center',
              width: '100%',
              fontWeight: 'bold',
              wordBreak: 'break-word'
            }}>
              {error}
            </div>
          )}
        </div>

        <div className="responsive-panel" style={{ 
          flex: 1, 
          position: 'relative', 
          display: 'flex', 
          flexDirection: 'column', 
          minWidth: 0,
          maxHeight: '100%',
          overflow: 'hidden'
        }}>
          <div style={{ 
            position: 'relative', 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }}>
            {(outputFormat === 'json' ? formattedJson : convertedOutput) && (
              <button
                onClick={handleCopyPre}
                style={{
                  position: 'absolute', top: 10, right: 10, zIndex: 10,
                  padding: '6px 14px', backgroundColor: copySuccess ? '#4CAF50' : '#00BFA5', color: 'white',
                  border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 500,
                  transition: 'background-color 0.3s',
                }}
              >
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
            )}
            <PreWithLines
              value={outputFormat === 'json' ? formattedJson : convertedOutput}
              preRef={outputRef}
              lineNumbersRef={outputLineNumbersRef}
              onScroll={handleOutputScroll}
              style={{ backgroundColor: '#fafafa', margin: 0 }}
              preProps={{ className: 'responsive-pre' }}
              lineNumbersRefClassName="responsive-linenumbers"
            />
          </div>
        </div>
      </div>
      
      <div style={{ maxWidth: '1200px', margin: '40px auto 20px', padding: '0 20px', fontSize: '15px', lineHeight: '1.6', color: '#444' }}>
        <h2 style={{ fontSize: '22px', marginBottom: '15px', color: '#333' }}>About JSON Beautifier</h2>
        <p style={{ marginBottom: '15px' }}>
          JSON (JavaScript Object Notation) is a lightweight data interchange format that's easy for humans to read and write, and easy for machines to parse and generate. However, when JSON is minified or compressed, it becomes difficult to read and edit manually.
        </p>
        <p style={{ marginBottom: '15px' }}>
          Our <strong>JSON Beautifier</strong> tool formats and prettifies your JSON data with proper indentation and spacing, making it much more readable. This is particularly useful when:
        </p>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}>Debugging JSON responses from APIs</li>
          <li style={{ marginBottom: '8px' }}>Reviewing configuration files in JSON format</li>
          <li style={{ marginBottom: '8px' }}>Editing manually created JSON data</li>
          <li style={{ marginBottom: '8px' }}>Learning or teaching JSON structure</li>
        </ul>
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>Key Features</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Format/Beautify</strong>: Indent your JSON with customizable tab spacing (2, 4, or 6 spaces)</li>
          <li style={{ marginBottom: '8px' }}><strong>Minify/Compact</strong>: Remove all whitespace for minimum file size</li>
          <li style={{ marginBottom: '8px' }}><strong>Validate</strong>: Check if your JSON is valid and error-free</li>
          <li style={{ marginBottom: '8px' }}><strong>Convert</strong>: Transform JSON to other formats like XML, YAML, CSV, C# Classes, or Java Classes</li>
          <li style={{ marginBottom: '8px' }}><strong>Line Numbers</strong>: See line numbers for easy reference</li>
          <li style={{ marginBottom: '8px' }}><strong>Upload & Download</strong>: Process JSON files and save the results</li>
        </ul>
        <p style={{ marginBottom: '15px' }}>
          Our tool processes all data locally in your browser, ensuring your data never leaves your computer. This makes it a secure option for handling sensitive JSON data.
        </p>
        <p>
          Whether you're a developer working with API responses, configuring applications, or simply trying to understand JSON data better, our JSON Beautifier provides a fast, free, and efficient way to make your JSON more readable and manageable.
        </p>
      </div>
      
    </>
  );
};

export default BeautifierPage;